import { ApplicationRef, Injectable, NgZone } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, concat, fromEvent, interval } from 'rxjs';
import { filter, first, throttleTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UpdatesService {
  public updatesSource = new BehaviorSubject<boolean>(false);
  public updatesCalled$ = this.updatesSource.asObservable();

  constructor(
    private readonly _appRef: ApplicationRef,
    private readonly _swUpdate: SwUpdate,
    private readonly _zone: NgZone
  ) {
    if (!this._swUpdate.isEnabled) return;

    const everyTenMin$ = interval(10 * 60 * 1000);
    const appIsStable$ = this._appRef.isStable.pipe(
      first((isStable) => isStable)
    );
    const onFocus$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
      throttleTime(60 * 1000)
    );

    this._zone.runOutsideAngular(() => {
      concat(appIsStable$, everyTenMin$).subscribe(() => this.safeCheck());
      onFocus$.subscribe(() => this.safeCheck());

      // Bridge DevTools "Update" / SW lifecycle into Angular's checkForUpdate.
      // Fires only when the SW script actually changes — usually no-op
      // between Angular deploys since ngsw-worker.js is byte-identical.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('controllerchange', () =>
          this.safeCheck()
        );
        navigator.serviceWorker.getRegistration().then((reg) => {
          reg?.addEventListener('updatefound', () => this.safeCheck());
        });
      }
    });

    this._swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY'
        )
      )
      .subscribe(() => this._zone.run(() => this.updatesSource.next(true)));
  }

  public checkUpdates(): void {
    if (!this._swUpdate.isEnabled) return;

    this.safeCheck();
  }

  public applyUpdate(): void {
    if (!this._swUpdate.isEnabled) {
      document.location.reload();
      return;
    }

    this._swUpdate.activateUpdate().finally(() => document.location.reload());
  }

  private safeCheck(): void {
    this._swUpdate.checkForUpdate().catch(() => {
      /* next tick */
    });
  }
}
